import { auth } from '../firebase';
import { refreshToken } from './auth';
import { databaseConfig } from '../config/database';

/**
 * API 錯誤類型
 */
interface ApiError extends Error {
  status?: number;
  response?: Response;
}

/**
 * 獲取 API Base URL
 */
const getApiBaseUrl = (): string => {
  return databaseConfig.api.url || process.env.REACT_APP_API_URL || 'http://localhost:8080';
};

/**
 * 獲取 Firebase ID Token
 * 如果 auth.currentUser 為 null，會等待最多 2 秒
 */
export const getIdToken = async (maxWaitMs: number = 2000): Promise<string | null> => {
  try {
    // 如果 auth.currentUser 為 null，等待一小段時間後重試
    let user = auth.currentUser;
    if (!user) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ auth.currentUser 為 null，等待用戶可用...');
      
      // 等待最多 maxWaitMs 毫秒
      const startTime = Date.now();
      while (!user && (Date.now() - startTime) < maxWaitMs) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 等待 100ms
        user = auth.currentUser;
      }
      
      if (!user) {
        // eslint-disable-next-line no-console
        console.error('❌ 等待超時，auth.currentUser 仍然為 null');
        return null;
      }
    }
    
    const token = await user.getIdToken();
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ getIdToken() 返回 null');
    }
    return token;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('❌ 獲取 ID Token 失敗:', error);
    return null;
  }
};

/**
 * 建立帶有認證的 fetch 請求
 * 使用 Firebase ID Token（支援 Google SSO 和 Email/Password）
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  try {
    // 如果 URL 是相對路徑，加上 API Base URL
    const fullUrl = url.startsWith('http') ? url : `${getApiBaseUrl()}${url}`;
    
    // 獲取 Firebase token（支援 Google SSO 和 Email/Password）
    const token = await getIdToken();
    
    // 如果沒有 token，直接拋出錯誤，不要發送請求
    if (!token) {
      // eslint-disable-next-line no-console
      console.error('❌ 無法獲取 Firebase token，請求已取消');
      throw new Error('未授權：無法獲取認證 token，請重新登入');
    }
    
    const headers = new Headers(options.headers || {});
    
    // 如果沒有 Content-Type，預設為 application/json
    if (!headers.has('Content-Type') && options.body) {
      headers.set('Content-Type', 'application/json');
    }
    
    // 添加 Authorization header
    headers.set('Authorization', `Bearer ${token}`);
    
    const response = await fetch(fullUrl, {
      ...options,
      headers
    });
    
    // 處理 401 錯誤（未授權）
    if (response.status === 401) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ API 請求未授權 (401)，嘗試刷新 token...');
      
      // 嘗試刷新 token 並重試一次
      const newToken = await refreshToken();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        const retryResponse = await fetch(fullUrl, {
          ...options,
          headers
        });
        
        if (retryResponse.status === 401) {
          // 重試後仍然失敗，拋出錯誤（但不自動登出）
          const errorData = await retryResponse.json().catch(() => ({}));
          // eslint-disable-next-line no-console
          console.error('❌ 重試後仍然未授權:', errorData);
          const error: ApiError = new Error(errorData.message || errorData.error || '未授權：請重新登入');
          error.status = 401;
          error.response = retryResponse;
          throw error;
        }
        
        return retryResponse;
      } else {
        // 無法刷新 token，拋出錯誤（但不自動登出）
        const errorData = await response.json().catch(() => ({}));
        // eslint-disable-next-line no-console
        console.error('❌ 無法刷新 token:', errorData);
        const error: ApiError = new Error(errorData.message || errorData.error || '未授權：請重新登入');
        error.status = 401;
        error.response = response;
        throw error;
      }
    }
    
    // 處理 403 錯誤（權限不足）
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      // eslint-disable-next-line no-console
      console.error('❌ API 請求權限不足 (403):', errorData);
      const error: ApiError = new Error(errorData.message || errorData.error || '權限不足');
      error.status = 403;
      error.response = response;
      throw error;
    }
    
    return response;
  } catch (error) {
    // 如果是網路錯誤或其他錯誤，直接拋出
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('API 請求失敗');
  }
};

/**
 * 建立帶有認證的 axios 請求配置
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

